import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useWorkflowContext } from '../../contexts/WorkflowContext';
import { LoadingSpinner, ErrorDisplay } from './StatusComponents';
import { LabelComponent } from './LabelComponent';
import { WorkflowDetails } from './WorkflowDetails';
import { ILabel } from '../../constants/interfaces';
import { getFieldDefaultValue } from '../../utils/formUtils';
import { getVisibleLabels } from './visibilityUtils';
import {
  fetchWorkflowLabels,
  submitWorkflowResponse,
  LabelResponsePayload,
} from '../../api/Workflows.client';
import { queryKeys } from '../../api/queryKeys';
import {
  areWorkflowLabelResponsesValid,
  getMissingMandatoryLabelTexts,
  validateWorkflowLabelResponses,
} from '../../utils/validation';
import {
  buttonContainerStyles,
  centeredTextStyles,
  getToastBackgroundColor,
  titleStyles,
  toastStyles,
  workflowFormFieldSlotStyles,
  workflowFormStackStyles,
  workflowResponseColumnStyles,
  workflowSummaryBandStyles,
} from './WorkflowResponseForm.styles';
import {
  requestFormHeaderRowSx,
  requestFormPageShellSx,
  requestFormPaperSx,
} from '../../styles/requestFormShell';
import BackButton from '../../components/BackButton';
import FormButtonGroup from '../../components/FormButtonGroup';
import NdcGroupedDocketsInfo from './NdcGroupedDocketsInfo';
import NodHeaderAndSummary from './NodHeaderAndSummary';
import { useWorkflowTypes } from '../WorkflowsPage/hooks/useWorkflowTypes';

interface WorkflowFormData {
  workflowId: string;
  type: string;
  accountId: string;
  caseNumber?: string;
  dismissalDate?: string;
  assignedDate: string;
  assignedTo?: string;
  assignedToName?: string;
  labelResponses: Record<number, string>; // labelId -> response value
  ndcGroupedDocketsInfo: string;
}

const WorkflowResponseForm: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const { workflowTypes } = useWorkflowTypes();
  const {
    allWorkflows,
    loading: workflowsLoading,
    error: workflowsError,
    getWorkflowById,
    refreshWorkflows,
  } = useWorkflowContext();
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>(
    'error'
  );
  const [labelErrors, setLabelErrors] = useState<Record<number, string>>({});
  const [formData, setFormData] = useState<WorkflowFormData>({
    accountId: '',
    assignedDate: '',
    assignedTo: '',
    assignedToName: '',
    caseNumber: '',
    dismissalDate: '',
    labelResponses: {},
    ndcGroupedDocketsInfo: '',
    type: '',
    workflowId: workflowId || '',
  });

  // Load workflow data from context
  useEffect(() => {
    if (!workflowId) {
      setError('No workflow ID provided');
      return;
    }

    if (workflowsError) {
      setError(workflowsError);
      return;
    }

    if (!workflowsLoading && allWorkflows.length > 0) {
      const workflow = getWorkflowById(workflowId);

      if (workflow) {
        setFormData({
          accountId: workflow.accountId,
          assignedDate: workflow.assignedDate,
          assignedTo: workflow.assignedTo || '',
          assignedToName: workflow.assignedToName || '',
          caseNumber: workflow.caseNumber,
          dismissalDate: workflow.dismissalDate || '',
          labelResponses: {},
          ndcGroupedDocketsInfo: workflow.ndcGroupedDockets || '',
          type: workflow.requestType || '',
          workflowId: workflow.id.toString(),
        });
        setError(null);
      } else if (!isSubmissionSuccessful) {
        // Only show "workflow not found" if we're not in the middle of a successful submission
        setToastMessage('Workflow not found');
        setToastSeverity('error');
        setShowToast(true);
        setTimeout(() => {
          navigate('/workflows');
        }, 2000); // Redirect after 2 seconds to allow user to see the message
      }
    }
  }, [
    workflowId,
    allWorkflows,
    workflowsLoading,
    workflowsError,
    getWorkflowById,
    isSubmissionSuccessful,
    navigate,
  ]);

  // Fetch labels when workflow type is set
  const {
    data: labelsData,
    isLoading: labelsLoading,
    isError: labelsIsError,
  } = useQuery({
    enabled: !!formData.type,
    queryFn: () => fetchWorkflowLabels(formData.type),
    queryKey: queryKeys.workflowLabels(formData.type),
    select: (data: ILabel[]) =>
      [...data].sort((a, b) => {
        const toInt = (v: string | number | undefined) =>
          typeof v === 'string' ? parseInt(v) || 0 : (v ?? 0);
        return toInt(a.orderIndex) - toInt(b.orderIndex);
      }),
  });

  const labels = useMemo(() => labelsData ?? [], [labelsData]);

  // Apply each label's default option once its labels arrive. The request form does
  // this through useFormLabels; without it here an option flagged isDefault renders
  // unselected, so a required dropdown with a single default still blocks submission.
  useEffect(() => {
    if (labels.length === 0) {
      return;
    }
    setFormData((prev) => {
      const seeded: Record<number, string> = {};
      labels.forEach((label) => {
        // Never overwrite something the user has already answered.
        if (prev.labelResponses[label.labelId]) {
          return;
        }
        const defaultValue = getFieldDefaultValue(label);
        if (defaultValue) {
          seeded[label.labelId] = defaultValue;
        }
      });
      if (Object.keys(seeded).length === 0) {
        return prev;
      }
      return {
        ...prev,
        labelResponses: { ...prev.labelResponses, ...seeded },
      };
    });
  }, [labels]);

  // Show toast when labels fail to load
  useEffect(() => {
    if (labelsIsError) {
      setToastMessage('Failed to load form fields');
      setToastSeverity('error');
      setShowToast(true);
    }
  }, [labelsIsError]);

  // Mutation for submitting the workflow response
  const submitMutation = useMutation({
    mutationFn: ({
      workflowIdNum,
      userResponses,
    }: {
      workflowIdNum: number;
      userResponses: LabelResponsePayload[];
    }) => submitWorkflowResponse(workflowIdNum, userResponses),
    onError: (err) => {
      console.error('Error submitting workflow response:', err);
      setToastMessage('Failed to submit workflow response. Please try again.');
      setToastSeverity('error');
      setShowToast(true);
    },
    onSuccess: async () => {
      setIsSubmissionSuccessful(true);
      setFormData((prev) => ({ ...prev, labelResponses: {} }));
      setLabelErrors({});
      setToastMessage('Workflow completed successfully!');
      setToastSeverity('success');
      setShowToast(true);
      try {
        await refreshWorkflows();
      } catch (refreshError) {
        console.error('Error refreshing workflows:', refreshError);
      }
      setTimeout(() => navigate('/workflows'), 1500);
    },
  });

  const submitting = submitMutation.isPending;

  // Calculate visible labels using useMemo to avoid infinite re-renders
  const visibleLabels = useMemo(() => {
    return getVisibleLabels(labels, formData.labelResponses);
  }, [labels, formData.labelResponses]);

  // Clean up errors and values for labels that are now hidden
  useEffect(() => {
    const visibleLabelIds = new Set(
      visibleLabels.map((label) => label.labelId)
    );

    // Clear errors for hidden labels
    setLabelErrors((prev) => {
      const newErrors = { ...prev };
      let hasChanges = false;
      Object.keys(newErrors).forEach((labelIdStr) => {
        const labelId = parseInt(labelIdStr);
        if (!visibleLabelIds.has(labelId)) {
          delete newErrors[labelId];
          hasChanges = true;
        }
      });
      return hasChanges ? newErrors : prev;
    });

    // Clear values for hidden labels
    setFormData((prev) => {
      const newLabelResponses = { ...prev.labelResponses };
      let hasChanges = false;
      Object.keys(newLabelResponses).forEach((labelIdStr) => {
        const labelId = parseInt(labelIdStr);
        if (!visibleLabelIds.has(labelId)) {
          delete newLabelResponses[labelId];
          hasChanges = true;
        }
      });
      return hasChanges
        ? {
            ...prev,
            labelResponses: newLabelResponses,
          }
        : prev;
    });
  }, [visibleLabels]);

  const handleLabelResponse = (labelId: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      labelResponses: {
        ...prev.labelResponses,
        [labelId]: value,
      },
    }));

    // Clear any existing error for this label
    setLabelErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[labelId];
      return newErrors;
    });
  };

  // Check if all mandatory visible fields are filled
  const isFormValid = () =>
    areWorkflowLabelResponsesValid(visibleLabels, formData.labelResponses);

  const getMissingMandatoryFields = () =>
    getMissingMandatoryLabelTexts(visibleLabels, formData.labelResponses);

  const submitWorkflow = () => {
    if (submitting) return 'Submitting...';
    const missingFields = getMissingMandatoryFields();
    if (missingFields.length > 0) {
      return `Please fill in required fields: ${missingFields.join(', ')}`;
    }
    return 'Submit workflow response';
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    // Validate mandatory labels (only visible ones)
    const errors = validateWorkflowLabelResponses(
      visibleLabels,
      formData.labelResponses
    );

    if (Object.keys(errors).length > 0) {
      setLabelErrors(errors);
      setToastMessage('Please fill in all required fields');
      setToastSeverity('error');
      setShowToast(true);
      return;
    }

    const userResponses: LabelResponsePayload[] = Object.entries(
      formData.labelResponses
    ).map(([labelIdStr, response]) => ({
      labelId: parseInt(labelIdStr),
      response,
    }));

    submitMutation.mutate({
      userResponses,
      workflowIdNum: parseInt(formData.workflowId),
    });
  };

  const handleBack = async (event?: React.MouseEvent) => {
    navigate('/workflows');

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleClear = () => {
    setFormData((prev) => ({
      ...prev,
      labelResponses: {},
    }));
    setLabelErrors({});
  };

  const handleToastClose = () => {
    setShowToast(false);
  };

  if (workflowsLoading) {
    return <LoadingSpinner loading={workflowsLoading} />;
  }

  if (error) {
    return <ErrorDisplay error={error} onBack={handleBack} />;
  }
  return (
    <Box sx={requestFormPageShellSx}>
      <Box sx={requestFormHeaderRowSx}>
        <BackButton onClick={handleBack} />
        <Typography
          variant="h4"
          component="h1"
          sx={{
            ...titleStyles,
            flex: 1,
            letterSpacing: '-0.01em',
            m: 0,
            textAlign: 'center',
          }}
        >
          {workflowTypes.find((wt) => wt.value === formData.type)
            ?.response_header || 'Workflow Response'}
        </Typography>
        {/* Balance layout with request forms (grid/list toggle slot) */}
        <Box sx={{ minWidth: { sm: 120, xs: 0 } }} aria-hidden />
      </Box>

      <Paper elevation={0} sx={requestFormPaperSx}>
        <Box sx={workflowSummaryBandStyles}>
          <NodHeaderAndSummary type={formData.type} />

          <WorkflowDetails
            accountId={formData.accountId}
            caseNumber={formData.caseNumber}
            dismissalDate={formData.dismissalDate}
            type={formData.type}
            assignedDate={formData.assignedDate}
            assignedToName={formData.assignedToName}
          />

          {formData.type === 'ndc_pacer_cases' &&
            formData.ndcGroupedDocketsInfo.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <NdcGroupedDocketsInfo
                  docketInfo={formData.ndcGroupedDocketsInfo}
                />
              </Box>
            )}
        </Box>

        <Box sx={workflowResponseColumnStyles}>
          {/* Response Form Section */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {/* Dynamic Labels Section */}
              {labelsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Loading form fields...
                  </Typography>
                </Box>
              )}

              {visibleLabels.length > 0 ? (
                <Box sx={workflowFormStackStyles}>
                  {visibleLabels.map((label) => (
                    <Box key={label.labelId} sx={workflowFormFieldSlotStyles}>
                      <LabelComponent
                        label={label}
                        value={formData.labelResponses[label.labelId] || ''}
                        onChange={handleLabelResponse}
                        error={labelErrors[label.labelId]}
                      />
                    </Box>
                  ))}
                </Box>
              ) : !labelsLoading && labels.length > 0 ? (
                <Box sx={centeredTextStyles}>
                  <Typography variant="body2" color="text.secondary">
                    No visible form fields for current selections.
                  </Typography>
                </Box>
              ) : !labelsLoading ? (
                <Box sx={centeredTextStyles}>
                  <Typography variant="body2" color="text.secondary">
                    No form fields available for this workflow type.
                  </Typography>
                </Box>
              ) : null}

              {/* Action Buttons */}
              <Box sx={buttonContainerStyles}>
                <FormButtonGroup
                  onClear={handleClear}
                  submitting={submitting}
                  disableSubmit={!isFormValid()}
                  submitTooltip={submitWorkflow()}
                />
              </Box>
            </Stack>
          </form>
        </Box>
      </Paper>

      {/* Toast for workflow status */}
      <Snackbar
        open={showToast}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
      >
        <Alert
          onClose={handleToastClose}
          severity={toastSeverity}
          sx={{
            ...toastStyles,
            backgroundColor: getToastBackgroundColor(toastSeverity),
            color: 'white',
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkflowResponseForm;
